import { useFrame } from '@react-three/fiber';
import { useMagnesiumOxideStore, TRIAL_PRESETS } from '../store/useMagnesiumOxideStore';
import LabRoom from '../../lab/shared/LabRoom';
import { TripodStand } from '../../lab/shared/TripodStand';
import { BunsenBurner } from '../../lab/shared/BunsenBurner';
import { ElectronicScale } from '../../lab/shared/ElectronicScale';
import { Crucible } from '../../lab/shared/Crucible';
import { CrucibleLid } from '../../lab/shared/CrucibleLid';
import { PipeclayTriangle } from '../../lab/shared/PipeclayTriangle';
import { MagnesiumRibbon } from '../../lab/shared/MagnesiumRibbon';
import { Sandpaper } from '../../lab/shared/Sandpaper';
import { MgRibbonSpool } from '../../lab/shared/MgRibbonSpool';
import { HeatproofMat } from '../../lab/shared/HeatproofMat';
import {
  PolishAnimation,
  PlaceMgAnimation,
  useCrucibleWorldPosition,
  useLidTransform,
  IgnitionFlash,
  WhiteFumes,
  AnimatedTongs,
  HeatShimmer,
} from './MagnesiumOxideAnimations';

export function MagnesiumOxideScene3D() {
  const labStage = useMagnesiumOxideStore(s => s.labStage);
  const currentTrial = useMagnesiumOxideStore(s => s.currentTrial);
  const currentReadingG = useMagnesiumOxideStore(s => s.currentReadingG);
  const mgInCrucibleG = useMagnesiumOxideStore(s => s.mgInCrucibleG);
  const mgOInCrucibleG = useMagnesiumOxideStore(s => s.mgOInCrucibleG);
  const crucibleTempC = useMagnesiumOxideStore(s => s.crucibleTempC);
  const isHeating = useMagnesiumOxideStore(s => s.isHeating);
  const heatIntensity = useMagnesiumOxideStore(s => s.heatIntensity);
  const lidIsOn = useMagnesiumOxideStore(s => s.lidIsOn);
  const isPolishingMg = useMagnesiumOxideStore(s => s.isPolishingMg);
  const polishProgress = useMagnesiumOxideStore(s => s.polishProgress);
  const isPlacingMg = useMagnesiumOxideStore(s => s.isPlacingMg);
  const placeMgProgress = useMagnesiumOxideStore(s => s.placeMgProgress);
  const isPlacingOnTripod = useMagnesiumOxideStore(s => s.isPlacingOnTripod);
  const placeOnTripodProgress = useMagnesiumOxideStore(s => s.placeOnTripodProgress);
  const isPlacingLid = useMagnesiumOxideStore(s => s.isPlacingLid);
  const lidPlaceProgress = useMagnesiumOxideStore(s => s.lidPlaceProgress);
  const isLiftingLid = useMagnesiumOxideStore(s => s.isLiftingLid);
  const lidLiftProgress = useMagnesiumOxideStore(s => s.lidLiftProgress);
  const isIgniting = useMagnesiumOxideStore(s => s.isIgniting);
  const ignitionProgress = useMagnesiumOxideStore(s => s.ignitionProgress);
  const isWeighing = useMagnesiumOxideStore(s => s.isWeighing);
  const weighProgress = useMagnesiumOxideStore(s => s.weighProgress);
  const weighType = useMagnesiumOxideStore(s => s.weighType);
  const tick = useMagnesiumOxideStore(s => s.tick);
  const toggleHeating = useMagnesiumOxideStore(s => s.toggleHeating);
  const setHeatIntensity = useMagnesiumOxideStore(s => s.setHeatIntensity);
  const simulationSpeed = useMagnesiumOxideStore(s => s.simulationSpeed);

  useFrame((_, delta) => {
    tick(delta * simulationSpeed);
  });

  const preset = TRIAL_PRESETS[currentTrial];
  const apparatusReady =
    labStage === 'apparatus-ready' ||
    labStage === 'heating-open' ||
    labStage === 'igniting' ||
    labStage === 'lid-on' ||
    labStage === 'final-heating' ||
    labStage === 'cooling' ||
    labStage === 'final-weighed' ||
    labStage === 'constant-mass' ||
    labStage === 'observed' ||
    labStage === 'completed';

  const cruciblePos = useCrucibleWorldPosition(
    isPlacingOnTripod,
    placeOnTripodProgress,
    isWeighing,
    weighProgress,
    weighType,
    apparatusReady,
  );

  const lidTransform = useLidTransform(
    isPlacingLid,
    lidPlaceProgress,
    isLiftingLid,
    lidLiftProgress,
    lidIsOn,
  );

  // White fumes are visible while the lid is being lifted (within open window)
  const fumesActive = isLiftingLid && lidLiftProgress > 0.25 && lidLiftProgress < 0.80;

  // Heat shimmer over the crucible whenever it glows
  const showShimmer = crucibleTempC > 350 && apparatusReady;

  // Whether to render a static dull ribbon on the polishing pad when nothing is happening yet
  const showStaticRibbonOnPad =
    !isPolishingMg && !isPlacingMg &&
    (labStage === 'setup' || labStage === 'crucible-weighed' || labStage === 'mg-polished');

  const ribbonPadState =
    labStage === 'crucible-weighed' ? 'dull'
    : labStage === 'mg-polished' ? 'polished'
    : 'dull';

  // Whether the ribbon coil is sitting inside the crucible (after place-mg or weighing)
  const burning = isIgniting && ignitionProgress < 0.95;

  return (
    <group>
      <LabRoom />

      {/* Main heating apparatus on the workbench */}
      <group position={[0, -0.585, 0]}>
        {/* Heatproof mat under the tripod to protect the bench */}
        <HeatproofMat position={[0, 0, -0.2]} size={0.55} />
        <TripodStand position={[0, 0, -0.2]} scale={0.6} />
        <PipeclayTriangle position={[0, 1.5 * 0.6, -0.2]} scale={0.95} />

        {/* Crucible — position controlled by the place-on-tripod animation */}
        <Crucible
          position={cruciblePos}
          temperatureC={crucibleTempC}
          contentMgG={mgInCrucibleG}
          contentMgOG={mgOInCrucibleG}
          mgCapacityG={preset.mgMassG}
          isBurning={burning}
        />

        {/* Lid — always rendered; lifted out of frame when not in use */}
        <CrucibleLid
          position={[cruciblePos[0], cruciblePos[1] + 0.05, cruciblePos[2]]}
          liftHeight={lidTransform.liftHeight}
          tiltZ={lidTransform.tiltZ}
          temperatureC={crucibleTempC}
        />

        <BunsenBurner
          position={[0, 0, -0.2]}
          scale={4}
          flameState={isHeating ? 'heating' : 'off'}
          intensity={heatIntensity}
          onToggle={toggleHeating}
          onAdjust={() => setHeatIntensity(heatIntensity >= 1.0 ? 0.2 : heatIntensity + 0.4)}
        />

        <IgnitionFlash
          active={isIgniting}
          progress={ignitionProgress}
          position={[cruciblePos[0], cruciblePos[1] + 0.04, cruciblePos[2]]}
        />

        <WhiteFumes
          active={fumesActive}
          position={[cruciblePos[0], cruciblePos[1] + 0.08, cruciblePos[2]]}
        />

        <HeatShimmer
          visible={showShimmer && !lidIsOn}
          position={[cruciblePos[0], cruciblePos[1] + 0.06, cruciblePos[2]]}
        />

        <AnimatedTongs
          visible={lidTransform.tongsVisible}
          position={lidTransform.tongsPos}
          tilt={lidTransform.tongsTilt}
          openAmount={0.2}
        />
      </group>

      {/* Electronic balance on the right workbench */}
      <group position={[1.0, -0.585, 0.4]}>
        <ElectronicScale
          position={[0, 0.04, 0]}
          rotation={[0, -Math.PI / 6, 0]}
          readingG={currentReadingG}
        />
      </group>

      {/* Polishing workbench (left side) */}
      <group position={[0, 0, 0]}>
        {/* Sandpaper sheet resting on the bench, used to polish the ribbon */}
        <Sandpaper position={[-1.2, -0.524, 0.2]} size={[0.18, 0.008, 0.11]} />

        {/* Mg ribbon spool sitting nearby for visual context */}
        <MgRibbonSpool position={[-1.45, -0.49, 0.05]} rotation={[0, Math.PI / 6, 0]} />

        {showStaticRibbonOnPad && (
          <MagnesiumRibbon
            position={[-1.2, -0.512, 0.2]}
            state={ribbonPadState as 'dull' | 'polished'}
            polishProgress={ribbonPadState === 'polished' ? 1 : 0}
            lengthCm={preset.ribbonLengthCm}
          />
        )}

        <PolishAnimation
          active={isPolishingMg}
          progress={polishProgress}
          ribbonLengthCm={preset.ribbonLengthCm}
        />

        <PlaceMgAnimation
          active={isPlacingMg}
          progress={placeMgProgress}
          ribbonLengthCm={preset.ribbonLengthCm}
        />
      </group>
    </group>
  );
}

export default MagnesiumOxideScene3D;
